import {Collapse, DatePicker, Form, Select, Input} from 'antd';
import {FinancialInstrumentPicker} from 'components/financialInstrumentPicker';
import styles from './newNotification.module.scss';
import FadeEffect from 'components/animation/fadeEffect';
import moment from 'moment';
import {
  audienceTypes,
  notificationFilterOptions,
  notificationLastVisitOptions,
  notificationOrderStatusOptions,
} from 'utils/constants';
import {
  InvestingExperienceEnum,
  investmentPreferenceEnum,
  investmentHorizonEnum,
  investmentAffiliationEnum,
  visaTypeEnum,
  citizenStatusEnum,
  annualNetIncomeEnum,
  taxFilingStatusEnum,
  employmentStatusEnum,
  estimatedNetWorthEnum,
  investmentGoalsEnum,
  investmentStrategiesEnum,
  kycStatusEnums,
} from 'utils/mock';
import {NotificationRecipientSelect} from 'components/notificationRecipientSelect/NotificationRecipientSelect';
import {getUserTimezone} from 'utils/helper';
import {useEffect} from 'react';
import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {GET_ALL_RISK_PROFILES} from 'graphql/queries/users';
import {
  WalletActivityFilter,
  WalletActivityHeader,
} from './WalletActivityFilter';

const NotificationFilter = ({
  audience,
  setAudience,
  recipients,
  setRecipients,
  defaultUsers,
  allGroups,
  allCampaigns,
  form,
  allTopics,
  allCountries,
  isEditMode = false,
}) => {
  const {Option} = Select;
  const width = 400;
  const signupDate = Form.useWatch('signupDate', form);

  // Watch duration unit to enable/disable value field
  const durationUnit = Form.useWatch('durationUnit', form);

  const {data: allRiskProfiles, loading: loadingRiskProfiles} = useQuery(
    GET_ALL_RISK_PROFILES,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error?.message),
    },
  );

  // Clear duration value when unit is cleared
  useEffect(() => {
    if (!durationUnit) {
      form.setFieldValue('durationValue', undefined);
    }
  }, [durationUnit, form]);

  return (
    <div>
      <div className={styles['audience-grid']}>
        <Form.Item
          rules={[
            {
              required: true,
              message: 'Audience required',
            },
          ]}
          name="audience"
          label="Audience"
          className={styles['fwidth']}
          initialValue={'ALL_USERS'}>
          <Select
            name="audience"
            value={audience}
            placeholder="All Users"
            defaultValue="ALL_USERS"
            onChange={e => setAudience(e)}>
            {audienceTypes.map(data => (
              <Option key={data.value}>{data.label}</Option>
            ))}
          </Select>
        </Form.Item>
      </div>
      {/* The recipient field carries no `name`: the recipient set lives in the
          composer's own state, not in the antd form. A named Form.Item would
          clone `value`/`onChange` onto the control and overwrite the ones
          passed here. */}
      {audience === 'USERS' && (
        <Form.Item className={styles['fwidth']} label="Recipients">
          <NotificationRecipientSelect
            value={recipients}
            onChange={setRecipients}
            defaultUsers={defaultUsers}
          />
        </Form.Item>
      )}
      {audience === 'USERS_BY_GROUP' && (
        <Form.Item
          rules={[{required: audience === 'USERS_BY_GROUP'}]}
          label="Groups"
          name="groups">
          <Select
            mode="tags"
            className={styles['fwidth']}
            placeholder="Select by Groups">
            {allGroups?.map(data => (
              <Option key={data?.id}>
                <div className={styles['name-data']}>
                  <img src={data?.bannerImg} alt="" />
                  <span>{data?.name}</span>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {audience === 'INVESTORS_IN_A_CAMPAIGN' && (
        <Form.Item label="Live Campaign" name="liveCampaignId">
          <Select className={styles['fwidth']} placeholder="Select by Campaign">
            {allCampaigns
              ?.filter(
                data =>
                  data?.campaignMode === 'LIVE' &&
                  data?.campaignStatus !== 'DRAFT',
              )
              ?.map(data => (
                <Option key={data?.id}>
                  <div className={styles['option-data']}>
                    <div className={styles['name-data']}>
                      <img src={data?.fundraisingCompany?.logoImgURL} alt="" />
                      <span>{data?.fundraisingCompany?.companyName}</span>
                    </div>
                    <div>--{data?.campaignStatus}</div>
                  </div>
                </Option>
              ))}
          </Select>
        </Form.Item>
      )}
      {audience === 'INVESTORS_WITH_RESERVATIONS' && (
        <Form.Item label="Test Campaign" name="testCampaignId">
          <Select className={styles['fwidth']} placeholder="Select by Campaign">
            {allCampaigns
              ?.filter(
                data =>
                  data?.campaignMode === 'TEST' &&
                  data?.campaignStatus !== 'DRAFT',
              )
              ?.map(data => (
                <Option key={data?.id}>
                  <div className={styles['option-data']}>
                    <div className={styles['name-data']}>
                      <img src={data?.fundraisingCompany?.logoImgURL} alt="" />
                      <span>{data?.fundraisingCompany?.companyName}</span>
                    </div>
                    <div>--{data?.campaignStatus}</div>
                  </div>
                </Option>
              ))}
          </Select>
        </Form.Item>
      )}
      <Form.Item label="Topics" name="topics">
        <Select
          mode="multiple"
          className={styles['fwidth']}
          placeholder="Select by Topics">
          {allTopics?.map(item => (
            <Option key={`${item?.name};${item?.id}`}>
              <div className={styles['name-data']}>
                <img src={item?.icon} alt="" />
                <span>{item?.name}</span>
              </div>
            </Option>
          ))}
        </Select>
      </Form.Item>
      {audience !== 'USERS' && (
        <FadeEffect>
          <div className={styles.accredited}>
            <Form.Item
              className={styles['field-width']}
              label="Accredited"
              name="accredited">
              <Select defaultValue={'EITHER'} placeholder="Either">
                {notificationFilterOptions.map(option => (
                  <Option key={option.value}>
                    <span>{option.label}</span>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['field-width']}
              label="Active Investors"
              name="activeInvestors">
              <Select defaultValue={'EITHER'} placeholder="Either">
                {notificationFilterOptions.map(option => (
                  <Option key={option.value}>
                    <span>{option.label}</span>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['field-width']}
              label="Verified Investors"
              name="verifiedInvestors">
              <Select defaultValue={'EITHER'} placeholder="Either">
                {notificationFilterOptions.map(option => (
                  <Option key={option.value}>
                    <span>{option.label}</span>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['field-width']}
              label="Latest App Visit"
              name="latestVisit">
              <Select defaultValue={'EITHER'} placeholder="Either">
                {notificationLastVisitOptions.map(option => (
                  <Option key={option.value}>
                    <span>{option.label}</span>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['field-width']}
              label="Profile Completion"
              name="profileCompletion">
              <Select defaultValue={'EITHER'} placeholder="Either">
                <Option key="EITHER">Either</Option>
                <Option key="EmailVerification">
                  Email Verification - Basics
                </Option>
                <Option key="PhoneVerification">
                  Phone Verification - Basics
                </Option>
                <Option key="Basics">Step One(Not Completed) - Basics</Option>
                <Option key="Preferences">
                  Step Two(Not Completed) - Preferences
                </Option>
                <Option key="Investments">
                  Step Three(Not Completed) - Investments
                </Option>
                <Option key="Customization">
                  Step Four(Not Completed) - Customization
                </Option>
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['field-width']}
              label="Daba Pro Members"
              name="isDabaPro">
              <Select defaultValue={'EITHER'} placeholder="Either">
                {notificationFilterOptions.map(option => (
                  <Option key={option.value}>
                    <span>{option.label}</span>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="isKycApproved"
              label="Identity Check Statuses"
              className={styles['field-width']}>
              <Select mode="multiple" placeholder="KYC Statuses" allowClear>
                {kycStatusEnums.map(({label, value}) => (
                  <Option key={value}>{label}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="isDocumentCheckApproved"
              label="Document Check Statuses"
              className={styles['field-width']}>
              <Select mode="multiple" placeholder="KYC Statuses" allowClear>
                {kycStatusEnums.map(({label, value}) => (
                  <Option key={value}>{label}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="isAmlApproved"
              label="AML Check Statuses"
              className={styles['field-width']}>
              <Select mode="multiple" placeholder="KYC Statuses" allowClear>
                {kycStatusEnums.map(({label, value}) => (
                  <Option key={value}>{label}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="riskProfile"
              className={styles['field-width']}
              label="Risk Profile">
              <Select
                mode="multiple"
                allowClear
                loading={loadingRiskProfiles}
                placeholder="Moderate"
                showSearch
                optionFilterProp="filter"
                filterOption={false}>
                {allRiskProfiles?.getAllRiskProfiles?.data?.map(data => (
                  <Option key={data.id}>{data.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </FadeEffect>
      )}
      <Collapse defaultActiveKey={isEditMode ? ['orderFilter'] : []}>
        <Collapse.Panel
          key="orderFilter"
          header={
            <h1 className={styles['header-text']}>
              Filter By Users Investment
            </h1>
          }>
          <h3>Investment Timeline</h3>
          <NotificationOrderByFilter form={form} />
        </Collapse.Panel>
      </Collapse>
      <Collapse defaultActiveKey={isEditMode ? ['walletFilter'] : []}>
        <Collapse.Panel
          key="walletFilter"
          header={<WalletActivityHeader className={styles['header-text']} />}>
          <WalletActivityFilter />
        </Collapse.Panel>
      </Collapse>
      <Collapse defaultActiveKey={isEditMode ? ['1'] : []}>
        <Collapse.Panel
          header={
            <h1 className={styles['header-text']}>
              Filter by Citizenship Details
            </h1>
          }
          key="1">
          <div className={styles['invest-form']}>
            <Form.Item label="Country of Residence" name="countryOfResidence">
              <Select mode="multiple" style={{width}} showSearch>
                {allCountries?.map(data => (
                  <Option key={data?.name}>
                    <div className={styles['all-data']}>
                      <img src={data?.icon} alt="" />
                      <span>{data?.name}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="State (Type state and click on Enter to type another input)"
              name="state">
              <Select mode="tags" className={styles['nwidth']} showSearch>
                <Option disabled>Please Input State</Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="City (Type city and click on Enter to type another input)"
              name="city">
              <Select mode="tags" className={styles['nwidth']} showSearch>
                <Option disabled>Please Input City</Option>
              </Select>
            </Form.Item>
            <Form.Item
              style={{width}}
              label="Country of Birth"
              name="countryOfBirth">
              <Select mode="multiple" className={styles['nwidth']} showSearch>
                {allCountries?.map(data => (
                  <Option key={`${data?.name};${data?.id}`}>
                    <div className={styles['all-data']}>
                      <img src={data?.icon} alt="" />
                      <span>{data?.name}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              style={{width}}
              label="Country of Citizenship"
              name="countryOfCitizenship">
              <Select mode="multiple" className={styles['nwidth']} showSearch>
                {allCountries?.map(data => (
                  <Option key={`${data?.name};${data?.id}`}>
                    <div className={styles['all-data']}>
                      <img src={data?.icon} alt="" />
                      <span>{data?.name}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="U.S Visa Type"
              name="visaType">
              <Select mode="tags">
                {visaTypeEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Citizenship Status"
              name="citizenStatus">
              <Select mode="tags">
                {citizenStatusEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </Collapse.Panel>
      </Collapse>
      <Collapse defaultActiveKey={isEditMode ? ['FinancialProfile'] : []}>
        <Collapse.Panel
          key="FinancialProfile"
          header={
            <h1 className={styles['header-text']}>
              Filter by Financial Profile
            </h1>
          }>
          <div className={styles['invest-form']}>
            <Form.Item
              className={styles['nwidth']}
              label="Annual Net Income"
              name="netIncome">
              <Select mode="tags">
                {annualNetIncomeEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Tax Filling Status"
              name="taxFilingStatus">
              <Select mode="tags">
                {taxFilingStatusEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Employment Status"
              name="employmentStatus">
              <Select mode="tags">
                {employmentStatusEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Estimated Net Worth"
              name="estimatedNetWorth">
              <Select mode="tags">
                {estimatedNetWorthEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </Collapse.Panel>
      </Collapse>
      <Collapse defaultActiveKey={isEditMode ? ['investorProfile'] : []}>
        <Collapse.Panel
          key="investorProfile"
          header={
            <h1 className={styles['header-text']}>
              Filter by Investor Profile
            </h1>
          }>
          <div className={styles['invest-form']}>
            <Form.Item
              className={styles['nwidth']}
              name="investmentExperience"
              label="Investment Experience">
              <Select mode="tags">
                {InvestingExperienceEnum.map(({name, key}) => (
                  <Option value={key} key={key}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Investment Preference"
              name="investmentPreference">
              <Select mode="tags">
                {investmentPreferenceEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Investment Horizon"
              name="investmentHorizon">
              <Select mode="tags">
                {investmentHorizonEnum.map(({name, key}) => (
                  <Option key={key}>{name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Investment Affiliation"
              name="investmentAffiliation">
              <Select mode="tags">
                {investmentAffiliationEnum.map(({name, key}) => (
                  <Option value={key} key={key}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Investment Goals"
              name="investmentGoals">
              <Select mode="tags">
                {investmentGoalsEnum.map(({name, key}) => (
                  <Option value={key} key={key}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles['nwidth']}
              label="Dominant Investment Strategies"
              name="investmentStrategies">
              <Select mode="tags">
                {investmentStrategiesEnum.map(({name, key}) => (
                  <Option value={key} key={key}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </Collapse.Panel>
      </Collapse>

      <Form.Item
        className={styles['nwidth']}
        label="Filter by Signup Time"
        name="signupDate">
        <Select
          allowClear
          placeholder="Choose how to filter users by signup time">
          <Option value="RANGE">By Date Range (From - To)</Option>
          <Option value="DURATION">By Time Ago (e.g., Last 30 days)</Option>
        </Select>
      </Form.Item>
      {signupDate === 'RANGE' && (
        <>
          <div>
            <h2>Signup Date Range</h2>
          </div>
          <div className={styles['schedule-grid']}>
            <Form.Item name="from" label="From">
              <DatePicker
                className={styles.date}
                name="From"
                format={'YYYY-MM-DD HH:mm:ss'}
                renderExtraFooter={() => (
                  <div style={{textAlign: 'center'}}>
                    <p>Your timezone is: {getUserTimezone()}</p>
                  </div>
                )}
                showTime
              />
            </Form.Item>
            <Form.Item name="to" label="To">
              <DatePicker
                className={styles.date}
                name="To"
                format={'YYYY-MM-DD HH:mm:ss'}
                renderExtraFooter={() => (
                  <div style={{textAlign: 'center'}}>
                    <p>Your timezone is: {getUserTimezone()}</p>
                  </div>
                )}
                showTime
              />
            </Form.Item>
          </div>
        </>
      )}
      {signupDate === 'DURATION' && (
        <>
          <div>
            <h2>Signup Duration</h2>
            <p
              style={{
                color: '#666',
                width: '50%',
                fontSize: '14px',
                marginBottom: '16px',
              }}>
              Filter users who signed up within a specific time period. For
              example, select &quot;Month&quot; and enter &quot;3&quot; to
              target users who signed up in the last 3 months.
            </p>
          </div>
          <div className={styles['schedule-grid']}>
            <Form.Item
              name="durationUnit"
              label="Duration Unit"
              style={{width: 200}}
              rules={[
                {
                  required: true,
                  message: 'Please select a time unit',
                },
              ]}>
              <Select placeholder="Select unit" allowClear showSearch={false}>
                <Option value="DAY">Day(s)</Option>
                <Option value="WEEK">Week(s)</Option>
                <Option value="MONTH">Month(s)</Option>
                <Option value="YEAR">Year(s)</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="durationValue"
              label="Duration Value"
              style={{width: 200}}
              rules={[
                {
                  required: true,
                  message: 'Please enter a value',
                },
                ({getFieldValue}) => ({
                  validator(_, value) {
                    const durationUnit = getFieldValue('durationUnit');
                    const numValue = Number(value);
                    if (
                      durationUnit &&
                      (value === undefined ||
                        value === null ||
                        String(value).trim() === '')
                    ) {
                      throw new Error(
                        'Value is required when unit is selected',
                      );
                    }
                    if (
                      durationUnit &&
                      (!Number.isInteger(numValue) ||
                        numValue <= 0 ||
                        isNaN(numValue))
                    ) {
                      throw new Error('Value must be a positive integer');
                    }
                    // eslint-disable-next-line no-undef
                    return Promise.resolve();
                  },
                }),
              ]}>
              <Input
                type="number"
                min="1"
                placeholder="e.g., 30"
                disabled={!durationUnit}
                style={{width: '100%'}}
              />
            </Form.Item>
            <div style={{width: 200}} />
          </div>
        </>
      )}
    </div>
  );
};

/* Asset classes that actually hold tradeable instruments. */
const ASSET_CLASS_OPTIONS = [
  {value: 'EQUITIES', label: 'Equities'},
  {value: 'FIXED_INCOMES', label: 'Fixed income'},
  {value: 'FUNDS', label: 'Funds'},
];

/* Which instrument families live under each class — the same routing the
   shared picker uses, so the two controls can never disagree. */
const INSTRUMENT_KEYS_BY_CLASS = {
  EQUITIES: [
    {value: 'PUBLIC_EQUITY', label: 'Stock / public equity'},
    {value: 'IPO', label: 'IPO'},
    {value: 'PRIVATE_EQUITY', label: 'Startup / private equity'},
  ],
  FIXED_INCOMES: [{value: 'BOND', label: 'Bond'}],
  FUNDS: [
    {value: 'MUTUAL_FUND', label: 'Mutual fund'},
    {value: 'VENTURE_FUND', label: 'Venture fund'},
  ],
};

export const NotificationOrderByFilter = ({form}) => {
  const audienceAssetClassKey = Form.useWatch('audienceAssetClassKey', form);
  const audienceInstrumentKey = Form.useWatch('audienceInstrumentKey', form);
  const audienceInstrumentId = Form.useWatch('audienceInstrumentId', form);

  // Asset class, family and instrument each take several values. The family
  // list follows every chosen class; exact instruments are searched within
  // one family, so they need exactly one family chosen.
  const chosenClasses = [].concat(audienceAssetClassKey ?? []);
  const chosenKeys = [].concat(audienceInstrumentKey ?? []);
  const chosenIds = [].concat(audienceInstrumentId ?? []);
  const instrumentKeysForClass = chosenClasses.flatMap(
    assetClass => INSTRUMENT_KEYS_BY_CLASS[assetClass] ?? [],
  );
  const singleFamily = chosenKeys.length === 1 ? chosenKeys[0] : undefined;

  return (
    <div className={styles['schedule-grid']}>
      <Form.Item name="orderFrom" label="Start Date">
        <DatePicker
          className={styles.date}
          format={'YYYY-MM-DD HH:mm:ss'}
          renderExtraFooter={() => (
            <div style={{textAlign: 'center'}}>
              <p>Your timezone is: {getUserTimezone()}</p>
            </div>
          )}
          showTime={{
            defaultValue: moment('00:00', 'HH:mm:ss'),
          }}
        />
      </Form.Item>
      <Form.Item name="orderTo" label="End Date">
        <DatePicker
          className={styles.date}
          format={'YYYY-MM-DD HH:mm:ss'}
          renderExtraFooter={() => (
            <div style={{textAlign: 'center'}}>
              <p>Your timezone is: {getUserTimezone()}</p>
            </div>
          )}
          showTime={{
            defaultValue: moment('00:00', 'HH:mm:ss'),
          }}
        />
      </Form.Item>
      <Form.Item
        className={styles.date}
        name="orderStatus"
        label="Order Status(es)">
        <Select mode="multiple" placeholder="Open" allowClear showSearch>
          {notificationOrderStatusOptions.map(option => (
            <Select.Option key={option.key}>{option.name}</Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        className={styles.date}
        name="orderDirection"
        label="Order Direction">
        <Select mode="multiple" placeholder="Any" allowClear showSearch>
          <Select.Option key="BUY">Buy</Select.Option>
          <Select.Option key="SELL">Sell</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item
        className={styles.date}
        name="isOrderOnHold"
        label="Order(s) On Hold">
        <Select defaultValue={'EITHER'} placeholder="Either">
          {notificationFilterOptions.map(option => (
            <Option key={option.value}>
              <span>{option.label}</span>
            </Option>
          ))}
        </Select>
      </Form.Item>
      {/* <div className={styles.date} /> */}
      <Form.Item name="orderType" label="Order Type" className={styles.date}>
        <Select mode="multiple" placeholder="Any" allowClear>
          <Option key="LIMIT">Limit</Option>
          <Option key="MARKET">Market</Option>
        </Select>
      </Form.Item>
      {/* Instrument v2 audience filters.
          `audience.assetClass` and `audience.assetId` are deprecated and
          rejected at runtime on dev. The canonical trio is asset class,
          instrument family, and exact instrument — and the notification
          service resolves the taxonomy itself from an exact instrument, so
          the admin never types a key. */}
      <Form.Item
        className={styles.date}
        name="audienceAssetClassKey"
        label="Asset class"
        tooltip="Broadest filter — everyone who ordered anything in this class.">
        <Select
          mode="multiple"
          placeholder="Any asset class"
          allowClear
          onChange={nextClasses => {
            // Keep the families that still belong to a chosen class.
            const allowed = []
              .concat(nextClasses ?? [])
              .flatMap(assetClass =>
                (INSTRUMENT_KEYS_BY_CLASS[assetClass] ?? []).map(o => o.value),
              );
            const keptKeys = chosenKeys.filter(key => allowed.includes(key));
            form.setFieldsValue({
              audienceInstrumentKey: keptKeys.length ? keptKeys : undefined,
              ...(keptKeys.length === chosenKeys.length
                ? {}
                : {audienceInstrumentId: undefined}),
            });
          }}>
          {ASSET_CLASS_OPTIONS.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        className={styles.date}
        name="audienceInstrumentKey"
        label="Instrument family"
        tooltip="Narrower — everyone who ordered this kind of instrument.">
        <Select
          mode="multiple"
          placeholder="Any instrument family"
          allowClear
          disabled={!chosenClasses.length}
          onChange={() =>
            form.setFieldsValue({audienceInstrumentId: undefined})
          }>
          {instrumentKeysForClass.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        className={styles.date}
        name="audienceInstrumentId"
        label="Exact instrument"
        tooltip="Narrowest — only the people who ordered one of these instruments.">
        <FinancialInstrumentPicker
          multiple
          targetKind={singleFamily}
          value={chosenIds}
          placeholder={
            singleFamily
              ? undefined
              : chosenKeys.length > 1
              ? 'Choose one instrument family to pick exact instruments'
              : 'Pick an instrument family first'
          }
          onChange={next =>
            form.setFieldsValue({
              audienceInstrumentId: next?.length ? next : undefined,
            })
          }
        />
      </Form.Item>
    </div>
  );
};

export default NotificationFilter;
